import { siteInfo } from '@/mocks/site';

interface JsonLdProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data?: Record<string, unknown>;
  pageUrl?: string;
  pageTitle?: string;
  pageDescription?: string;
  breadcrumbs?: { name: string; url: string }[];
  articleData?: {
    headline: string;
    description: string;
    datePublished: string;
    author: string;
    url: string;
  };
  faqData?: { question: string; answer: string }[];
}

export default function JsonLd({
  type,
  data,
  pageUrl,
  pageTitle,
  pageDescription,
  breadcrumbs,
  articleData,
  faqData,
}: JsonLdProps) {
  let schema: Record<string, unknown>;

  switch (type) {
    case 'Organization':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: siteInfo.url,
        description: siteInfo.description,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'CN',
          addressLocality: '北京市',
          addressRegion: '北京',
          streetAddress: siteInfo.address,
        },
        foundingDate: `${siteInfo.foundingYear}`,
        sameAs: [
          siteInfo.socialLinks.weibo,
          siteInfo.socialLinks.qq,
        ],
        ...data,
      };
      break;

    case 'WebSite':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: `${siteInfo.name} - ${siteInfo.tagline}`,
        url: siteInfo.url,
        description: siteInfo.description,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteInfo.url}/search?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
        inLanguage: 'zh-CN',
        ...data,
      };
      break;

    case 'WebPage':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: pageTitle || siteInfo.name,
        url: pageUrl || siteInfo.url,
        description: pageDescription || siteInfo.description,
        inLanguage: 'zh-CN',
        isPartOf: {
          '@type': 'WebSite',
          name: siteInfo.name,
          url: siteInfo.url,
        },
        ...data,
      };
      break;

    case 'BreadcrumbList':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: (breadcrumbs || []).map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
        ...data,
      };
      break;

    case 'Article':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleData?.headline || pageTitle,
        description: articleData?.description || pageDescription,
        datePublished: articleData?.datePublished,
        author: {
          '@type': 'Organization',
          name: articleData?.author || siteInfo.name,
        },
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleData?.url || pageUrl,
        },
        inLanguage: 'zh-CN',
        ...data,
      };
      break;

    case 'FAQPage':
      schema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (faqData || []).map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
        ...data,
      };
      break;

    default:
      schema = {};
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }}
    />
  );
}